import { redirect, type LoaderFunctionArgs } from "react-router";
import { marketingLinks, MarketingShell, socialImageMeta } from "../components/marketing/layout";
import { QuoteMotif } from "../components/marketing/QuoteMotif";
import {
  ArrowRight,
  CheckCircle,
  FileText,
  SendLink,
  Handshake,
  Scan,
  Sparkles,
  Buildings,
  ShieldCheck,
  Layers,
} from "../components/marketing/icons";
import { SITE_ORIGIN } from "../brand";

const APP_STORE_URL = "https://apps.shopify.com/quote-desk";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  if (url.searchParams.get("shop")) {
    throw redirect(`/app?${url.searchParams.toString()}`);
  }
  return null;
};

export const links = marketingLinks;

export const meta = () => [
  { title: "B2B Quote Desk for Shopify — Quotes and PO Parsing" },
  {
    name: "description",
    content:
      "B2B quote workflow for Shopify without Plus: negotiated per-quote pricing, shareable quote links, PO PDF parsing, and an AI counter-offer engine.",
  },
  {
    name: "keywords",
    content:
      "Shopify B2B quotes, PO PDF parser Shopify, AI counter-offer wholesale, draftOrder Shopify B2B, Shopify B2B without Plus",
  },
  { name: "robots", content: "index,follow" },
  { property: "og:title", content: "AI B2B Quote Desk for Shopify" },
  {
    property: "og:description",
    content:
      "B2B quote workflow for Shopify Basic, Grow, Advanced — no Plus required. Negotiated per-quote pricing + PO PDF parsing + AI counter-offers.",
  },
  { property: "og:type", content: "website" },
  { property: "og:url", content: `${SITE_ORIGIN}/` },
  ...socialImageMeta(),
  {
    tagName: "link",
    rel: "canonical",
    href: `${SITE_ORIGIN}/`,
  },
  {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "AI B2B Quote Desk + PO Parser",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Shopify",
      offers: {
        "@type": "AggregateOffer",
        priceCurrency: "USD",
        lowPrice: "29",
        highPrice: "79",
        offerCount: 3,
      },
      publisher: { "@type": "Organization", name: "Legitimate LLC" },
      about: [
        "Shopify B2B quotes",
        "PO PDF parser Shopify",
        "AI counter-offer engine",
        "Shopify B2B without Plus",
        "draftOrderCreate workflow",
      ],
    },
  },
];

const STEPS = [
  {
    icon: <FileText />,
    title: "Build the quote",
    body: "Pick a company from your Shopify catalog, add line items, and set negotiated per-line pricing. Or drop a PO PDF and let the parser pre-fill it.",
  },
  {
    icon: <SendLink />,
    title: "Send a link",
    body: "One click generates a public quote link at /q/<token>. Share it by email, Slack, or any channel — your buyer never needs a Shopify account.",
  },
  {
    icon: <Handshake />,
    title: "Buyer accepts → real order",
    body: "The buyer clicks Accept and Quote Desk calls draftOrderCreate to make a real Shopify order. They pay through your normal checkout.",
  },
];

const FEATURES = [
  {
    icon: <FileText />,
    title: "Quote → accept → draft order",
    body: "The full B2B loop: negotiated per-quote pricing, public quote links, one-click buyer accept, and a real Shopify order via draftOrderCreate. Every plan.",
    tier: null,
  },
  {
    icon: <Scan />,
    title: "PO PDF parsing",
    body: "Drop a purchase order PDF. The parser extracts line items, quantities, and prices with confidence-scored matching to your catalog — a 15-minute job in seconds.",
    tier: "Pro",
  },
  {
    icon: <Sparkles />,
    title: "AI counter-offers",
    body: "When a buyer counters, the engine reads their order history and your margin floor, then drafts a reasonable counter-counter you approve before it sends.",
    tier: "Growth",
  },
  {
    icon: <Buildings />,
    title: "Company directory",
    body: "Track repeat B2B buyers — multiple contacts, ship-to locations, and quote history per company, pulled straight from your Shopify companies.",
    tier: null,
  },
  {
    icon: <Layers />,
    title: "Bulk import & export",
    body: "CSV bulk import to build quotes fast, filtered export for finance, branded quote PDFs, and unlimited quotes on every tier.",
    tier: null,
  },
  {
    icon: <ShieldCheck />,
    title: "Verification harness",
    body: "Replay your current pricing rules against past orders to prove totals still match before you roll out a change. Confidence, on the record.",
    tier: "Growth",
  },
];

const STATS = [
  { num: "$2,300/mo", label: "What Shopify Plus B2B starts at — Quote Desk starts at $29" },
  { num: "/q/<token>", label: "Public quote links your buyers accept without a login" },
  { num: "14 days", label: "Free trial on every plan, including the AI engine" },
];

export default function PublicIndex() {
  return (
    <MarketingShell>
      {/* ---- Hero ---- */}
      <section className="qd-hero">
        <div className="qd-wrap qd-hero__grid">
          <div className="qd-reveal" data-d="1">
            <p className="qd-eyebrow">For Shopify Basic · Grow · Advanced</p>
            <h1>
              The B2B sales layer Shopify reserves for <em>Plus</em>. On every plan.
            </h1>
            <p className="qd-hero__lede">
              Quote Desk runs the whole wholesale loop without Shopify Plus:
              negotiated per-quote pricing, a public link your buyer accepts in
              one click, PO PDF parsing, and AI counter-offers drafted from order
              history.
            </p>
            <div className="qd-hero__cta">
              <a className="qd-btn" href={APP_STORE_URL}>
                See it on the App Store
                <ArrowRight />
              </a>
              <a className="qd-btn qd-btn--ghost" href="/features">
                Explore features
              </a>
            </div>
            <div className="qd-hero__note">
              <span>
                <CheckCircle />
                Listing returns Jun 12
              </span>
              <span>
                <CheckCircle />
                14-day free trial
              </span>
              <span>
                <CheckCircle />
                No Plus required
              </span>
            </div>
          </div>

          <div className="qd-reveal" data-d="2">
            <QuoteMotif />
          </div>
        </div>
      </section>

      {/* ---- Trust strip ---- */}
      <section className="qd-section--tight">
        <div className="qd-wrap qd-trust">
          <span className="qd-trust__label">The B2B essentials, unbundled from Plus</span>
          <span className="qd-tag">Quotes &amp; accept flow</span>
          <span className="qd-tag">Negotiated per-line pricing</span>
          <span className="qd-tag">PO PDF parsing</span>
          <span className="qd-tag">AI counter-offers</span>
          <span className="qd-tag">Company directory</span>
        </div>
      </section>

      {/* ---- How it works ---- */}
      <section className="qd-section qd-band">
        <div className="qd-wrap">
          <div className="qd-head qd-head--center qd-reveal" data-d="1">
            <p className="qd-eyebrow qd-eyebrow--center">How it works</p>
            <h2>From quote to paid order in three steps</h2>
            <p>
              No separate wholesale storefront, no migration, no Plus upgrade.
              Quote Desk sits on the Shopify admin you already use.
            </p>
          </div>
          <ol className="qd-steps" style={{ listStyle: "none" }}>
            {STEPS.map((s, i) => (
              <li
                className="qd-step qd-reveal"
                data-d={String(i + 1)}
                key={s.title}
              >
                <span className="qd-step__n" />
                <span className="qd-step__icon">{s.icon}</span>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ---- Stats ---- */}
      <section className="qd-section--tight">
        <div className="qd-wrap qd-stats">
          {STATS.map((s) => (
            <div className="qd-stat" key={s.label}>
              <div className="qd-stat__num">{s.num}</div>
              <p className="qd-stat__label">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---- Features ---- */}
      <section className="qd-section">
        <div className="qd-wrap">
          <div className="qd-head qd-reveal" data-d="1">
            <p className="qd-eyebrow">Everything in the box</p>
            <h2>One app for the entire B2B quote workflow</h2>
            <p>
              Most merchants stack three or four apps to do this. Quote Desk
              does it in one — and adds parsing and negotiation tools nobody
              else bundles at this price.
            </p>
          </div>
          <div className="qd-cards" style={{ marginTop: "2.6rem" }}>
            {FEATURES.map((f) => (
              <article className="qd-card" key={f.title}>
                {f.tier ? (
                  <span
                    className={`qd-card__tier qd-badge ${
                      f.tier === "Growth" ? "qd-badge--growth" : "qd-badge--pro"
                    }`}
                  >
                    {f.tier}
                  </span>
                ) : null}
                <span className="qd-card__icon">{f.icon}</span>
                <h3>{f.title}</h3>
                <p>{f.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ---- Pricing teaser ---- */}
      <section className="qd-section qd-band">
        <div className="qd-wrap">
          <div className="qd-head qd-head--center qd-reveal" data-d="1">
            <p className="qd-eyebrow qd-eyebrow--center">Pricing</p>
            <h2>Three plans. The whole B2B engine on every one.</h2>
            <p>
              Start at $29/mo for the full quote-to-order workflow. Add PO PDF
              parsing at Pro, and the AI counter-offer engine at Growth. Every
              tier ships with a 14-day free trial.
            </p>
          </div>
          <div className="qd-stats" style={{ marginTop: "2.6rem" }}>
            {[
              { name: "Starter", price: "$29", note: "Full quote → accept workflow" },
              { name: "Pro", price: "$49", note: "+ PO PDF → draft order parsing" },
              { name: "Growth", price: "$79", note: "+ AI counter-offers + verification" },
            ].map((p) => (
              <div className="qd-stat" key={p.name}>
                <div className="qd-stat__num">{p.price}</div>
                <p className="qd-stat__label">
                  <strong style={{ color: "var(--ink-800)" }}>{p.name}</strong>
                  <br />
                  {p.note}
                </p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: "2.4rem" }}>
            <a className="qd-btn" href="/pricing">
              See full pricing
              <ArrowRight />
            </a>
          </div>
        </div>
      </section>

      {/* ---- Closing CTA ---- */}
      <section className="qd-section">
        <div className="qd-wrap">
          <div className="qd-cta qd-reveal" data-d="1">
            <h2>Give your wholesale buyers a Plus-grade experience</h2>
            <p>
              Quotes, parsing, and negotiation — at a thirtieth of the price of
              Shopify Plus B2B. Quote Desk is available on the Shopify App
              Store now.
            </p>
            <div className="qd-cta__actions">
              <a className="qd-btn" href={APP_STORE_URL}>
                Visit the App Store listing
                <ArrowRight />
              </a>
              <a className="qd-btn qd-btn--ghost" href="/support">
                Email the founder
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
